import React from 'react';

// Firmware can only be pushed to players running a build that understands a
// signed update authorisation. Anything older ignores it, so offering the button
// would just look broken.
//
// Set this to the version that first shipped the authorisation handling.
export const MIN_FIRMWARE_UPDATE_VERSION = '1.11.2';

function parts(version) {
    if (typeof version !== 'string' || !/^[0-9]+(\.[0-9]+)*$/.test(version.trim())) {
        return null;
    }
    return version.trim().split('.').map(Number);
}

// -1, 0, 1, or null if either side isn't a version we can compare
export function compareVersions(a, b) {
    const pa = parts(a);
    const pb = parts(b);
    if (!pa || !pb) return null;

    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const na = pa[i] || 0;
        const nb = pb[i] || 0;
        if (na !== nb) return na > nb ? 1 : -1;
    }
    return 0;
}

// a player that has never reported a version is treated as too old - it either
// predates the reporting, or has not checked in since it was added
export function canReceiveFirmwareUpdate(player) {
    if (!player || !player.reportedVersion) return false;
    const cmp = compareVersions(player.reportedVersion, MIN_FIRMWARE_UPDATE_VERSION);
    return cmp !== null && cmp >= 0;
}

// A player acts on an authorisation at its next check-in, not when the button is
// clicked, and refuses anything in the first fifteen minutes of a boot. Check-in
// intervals run from one minute to twenty-four hours, so a fixed "a few minutes"
// is plainly wrong for a player on a long one.
//
// This is an upper bound, not a prediction: the check-in lands somewhere between
// now and the value returned.
export const MIN_UPTIME_BEFORE_UPDATE_MS = 15 * 60 * 1000;

export function expectedUpdateBy(player, now = Date.now()) {
    // an offline player will not check in at all, and a confident time beside one
    // that has been dark since yesterday is worse than saying nothing
    if (!player || !player.online || !player.lastSeen) return null;

    const lastSeen = Date.parse(player.lastSeen);
    const interval = Number(player.refreshInterval);
    if (!Number.isFinite(lastSeen) || !Number.isFinite(interval) || interval <= 0) {
        return null;
    }

    let due = lastSeen + interval;

    // reportedUptime is seconds, measured at lastSeen, so it dates the boot
    const uptime = Number(player.reportedUptime);
    if (Number.isFinite(uptime) && uptime >= 0) {
        due = Math.max(due, lastSeen - uptime * 1000 + MIN_UPTIME_BEFORE_UPDATE_MS);
    }

    // already overdue: fall back to wording that promises nothing
    return due <= now ? null : new Date(due);
}

// i18next interpolates plain strings, so a value cannot carry markup on its own.
// Wrapping it in a sentinel at interpolation time and splitting on that here lets
// a version be emphasised without breaking the sentence out of the translation
// file into JSX, which is where word order goes to die.
const MARK = '\u0000';

export function mark(value) {
    return MARK + value + MARK;
}

export function emphasised(text) {
    return text
        .split(MARK)
        .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}
